import React, { useState } from 'react';
import { Calculator, Copy, Trash2, DollarSign, Eye, Info } from 'lucide-react';

const CpmCalculatorTool = () => {
  const [totalCost, setTotalCost] = useState('');
  const [impressions, setImpressions] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const cost = parseFloat(totalCost);
    const imps = parseFloat(impressions);

    if (isNaN(cost) || isNaN(imps) || cost < 0 || imps <= 0) {
      setResult(null);
      return;
    }

    // CPM = (Total Cost / Impressions) × 1000
    const cpm = (cost / imps) * 1000;
    const costPerImpression = cost / imps;

    setResult({
      totalCost: cost,
      impressions: imps,
      cpm,
      costPerImpression
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setTotalCost('');
    setImpressions('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Determine CPM rating
  const getCpmRating = (cpm) => {
    if (cpm < 2) return { label: 'Excellent', color: 'text-green-400' };
    if (cpm < 5) return { label: 'Good', color: 'text-blue-400' };
    if (cpm < 10) return { label: 'Average', color: 'text-yellow-400' };
    if (cpm < 20) return { label: 'Above Average', color: 'text-orange-400' };
    return { label: 'High', color: 'text-red-400' };
  };

  const benchmarks = [
    { platform: 'Google Display', range: '$2 - $5' },
    { platform: 'Facebook/Instagram', range: '$5 - $15' },
    { platform: 'LinkedIn', range: '$10 - $30' },
    { platform: 'YouTube', range: '$5 - $20' },
    { platform: 'Programmatic', range: '$1 - $5' },
    { platform: 'Native Ads', range: '$5 - $15' },
  ];

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <DollarSign size={18} className="text-green-400" />
          <h3 className="text-sm font-medium text-gray-300">Campaign Data</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Cost ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              placeholder="e.g., 500"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="total-cost-input"
            />
            <p className="text-xs text-gray-500 mt-1">Total ad spend for the campaign</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Impressions
            </label>
            <input
              type="number"
              value={impressions}
              onChange={(e) => setImpressions(e.target.value)}
              placeholder="e.g., 100000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="impressions-input"
            />
            <p className="text-xs text-gray-500 mt-1">Number of times ad was displayed</p>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-cpm-btn"
      >
        <Calculator size={18} />
        Calculate CPM
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="cpm-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Eye size={18} className="text-blue-400" />
                <span className="text-sm text-gray-400">Cost Per 1,000 Impressions (CPM)</span>
              </div>
              <button
                onClick={() => copyResult(result.cpm, 'cpm')}
                className={`p-1.5 rounded transition-colors ${
                  copied === 'cpm' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-cpm"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="text-4xl font-bold text-green-400" data-testid="cpm-result">
              {formatCurrency(result.cpm)}
            </div>
            <div className={`text-sm mt-1 ${getCpmRating(result.cpm).color}`}>
              {getCpmRating(result.cpm).label} CPM
            </div>
          </div>

          {/* Secondary Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Cost Per Impression</div>
              <div className="text-lg font-bold text-blue-400">
                ${result.costPerImpression.toFixed(6)}
              </div>
            </div>
            <div className="p-3 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Impressions per $1</div>
              <div className="text-lg font-bold text-purple-400">
                {formatNumber(Math.round(1 / result.costPerImpression))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Cost</span>
                <span className="text-white font-medium">{formatCurrency(result.totalCost)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Impressions</span>
                <span className="text-white font-medium">{formatNumber(result.impressions)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">CPM (Cost ÷ Impressions × 1000)</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.cpm)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* What is CPM */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Info size={18} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">What is CPM?</h3>
        </div>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            <span className="text-white font-medium">CPM (Cost Per Mille)</span> measures the cost of 1,000 
            advertising impressions. "Mille" is Latin for thousand. It's a standard metric for comparing 
            the cost-efficiency of different ad campaigns and platforms.
          </p>
          <div className="p-3 bg-gray-900 rounded">
            <div className="text-xs text-gray-500 mb-2">Formula:</div>
            <div className="font-mono text-yellow-400">
              CPM = (Total Cost ÷ Total Impressions) × 1,000
            </div>
          </div>
          <p>
            <span className="text-white font-medium">When to use CPM:</span> CPM is best for brand awareness 
            campaigns where visibility matters more than clicks or conversions. It helps you understand how 
            much you're paying for exposure.
          </p>
        </div>
      </div>

      {/* Industry Benchmarks */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-4">Industry CPM Benchmarks</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {benchmarks.map((item, index) => (
            <div key={index} className="flex justify-between items-center p-2 bg-gray-900 rounded">
              <span className="text-sm text-gray-400">{item.platform}</span>
              <span className="text-sm font-medium text-green-400">{item.range}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Note: CPM varies significantly based on targeting, industry, ad format, and seasonality. 
          These are general ranges and your actual CPM may differ.
        </p>
      </div>

      {/* Related Metrics */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Related Advertising Metrics</h3>
        <div className="text-sm text-gray-400 space-y-2">
          <div><span className="text-white">CPC</span> - Cost Per Click: What you pay per ad click</div>
          <div><span className="text-white">CPA</span> - Cost Per Acquisition: What you pay per conversion</div>
          <div><span className="text-white">CTR</span> - Click-Through Rate: Clicks divided by impressions</div>
          <div><span className="text-white">ROAS</span> - Return On Ad Spend: Revenue divided by ad cost</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-cpm-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CpmCalculatorTool;
